import ExternalTypeLink from '../core/ExternalTypeLink';
import Errored from '../core/Errored';
import PropTypes from 'prop-types';
import React from 'react';
import Loading from '../core/Loading';
import TitledPanel from '../core/TitledPanel';
import { getProjectFromGraph, makeCancelable, sortObjects } from '../core/utils/utils';
import GS from '../grafsync/grafsync';
import CommonIriDisplay from '../application-core/CommonIriDisplay';
import AddIndividualClassDialog from './AddIndividualClassDialog';
import { APPLICATION_PATHS } from '../application-core/constants';

const loadOptions = {};

// Just in case import allows them in!
const TYPES_TO_IGNORE = [
  "NamedIndividual",
  "OwlClass",
  "ObjectProperty",
  "FunctionalProperty",
  "InverseFunctionalProperty",
  "SymmetricProperty",
  "AsymmetricProperty"
];

class IndividualTypeDisplay extends React.Component {

  constructor(props) {
    super(props);
    this.state = {
      isLoading: true,
      totalRelations: 0,
      resources: undefined,
      isErrored: false
    };
    this.cancellable = null;
  }

  static getDerivedStateFromProps(props, state) {
    const valuesLength = Array.isArray(props.values) ? props.values.length : (props.values ? 1 : 0);
    if (!state.isLoading && props.values && valuesLength !== state.totalRelations) {
      return {
        resources: undefined,
        isLoading: true,
        totalRelations: valuesLength
      };
    }
    return null;
  }

  componentDidUpdate() {
    const valuesLength = Array.isArray(this.props.values) ? this.props.values.length : (this.props.values ? 1 : 0);
    if (!this.state.isErrored && this.state.resources === undefined && this.props.values && valuesLength > 0) {
      this.collectResourceData();
    }
  }

  componentDidMount() {
    const valuesLength = Array.isArray(this.props.values) ? this.props.values.length : (this.props.values ? 1 : 0);
    if (this.props.values && valuesLength > 0) {
      this.collectResourceData();
    }
    else {
      if (this.cancellable && this.cancellable.isCancelled()) return;
      this.setState({ isLoading: false });
    }
  }

  loadData(iris, options) {
    this.cancellable = makeCancelable(GS.loadData(iris, options, this.props.graph));
    return this.cancellable;
  }

  componentWillUnmount() {
    if (this.cancellable) this.cancellable.cancel();
  }

  async collectResourceData() {
    try {
      if (this.cancellable && this.cancellable.isCancelled()) return;
      const valueList = Array.isArray(this.props.values) ? this.props.values : [this.props.values];
      const iris = valueList.filter(v => !TYPES_TO_IGNORE.includes(v));
      GS.handle("collect-data");
      if (iris.length > 0) {
        await this.loadData(iris, loadOptions).promise;
      }
      if (this.cancellable && this.cancellable.isCancelled()) return;
      this.processResources();
      this.setState({ isLoading: false });
    }
    catch (e) {
      if (!this.cancellable || (this.cancellable && !this.cancellable.isCancelled())) this.setState({ isErrored: e });
    }
  }

  processResources() {
    let valueList = Array.isArray(this.props.values) ? this.props.values : [this.props.values];
    let resources = valueList.map(id => {
      if (!GS.isLoaded(id, this.props.graph)) {
        return { id, notFound: true };
      }
      return GS.data(id, this.props.graph, false);
    });
    sortObjects(resources, 'label', this.props.viewLang, this.props.defaultLang, false);
    if (this.cancellable && this.cancellable.isCancelled()) return;
    this.setState({ resources });
  }

  monitorLinkUpdate = (iri, property) => {
    if (this.cancellable && this.cancellable.isCancelled()) return;
    if (property === "label") this.setState({ resources: undefined });
  };

  render() {

    const { id, isLoading, isReadOnly, graph, defaultLang, onAddClass, viewLang, textRef, sourceComponent,
      components, projectIRI, values, source } = this.props;
    const { resources, isErrored } = this.state;

    if (isErrored) return <Errored dataTest="ni-type-error" data={isErrored} />;
    if (isLoading) return <Loading />;

    const valueList = Array.isArray(values) ? values : [values];
    const iris = valueList.filter(v => !TYPES_TO_IGNORE.includes(v));

    const output = resources && resources
    .filter(resource => resource.inNamedGraph === graph || resource.id === "NamedIndividual")
    .map((resource) => {
      if (resource.notFound) {
        return <ExternalTypeLink
          key={resource.id}
          id={resource.id}
          compact={true}
          graph={graph}
          source={id}
          link={APPLICATION_PATHS.ONTOLOGY_MANAGER + '/project/ontology#project=' + getProjectFromGraph(graph)
            + "&ontology=" + sourceComponent + "&view=class"}
          removeTypeProperty="hasNamedIndividual"
          highlight={TYPES_TO_IGNORE.includes(resource.id) && resource.id !== "NamedIndividual" && resource.id !== "OwlClass"}
          isReadOnly={isReadOnly
            || resource.id === "OwlClass"
            || (resource.id === "NamedIndividual" && !resources.some(r => r.id === "OwlClass"))}
        />;
      }
      return <CommonIriDisplay
        key={resource.id}
        id={resource.id}
        compact={true}
        wrap={false}
        components={components}
        projectIRI={projectIRI}
        handleNotAvailable={true}
        source={source}
        property="type"
        graph={graph}
        viewLang={viewLang}
        defaultLang={defaultLang} />;
    });

    return (
      <TitledPanel dataTest="local-classes" height="full" title="Classes" actions=
        {!isReadOnly ?
          <AddIndividualClassDialog
            textRef={textRef}
            graph={graph}
            sourceComponent={sourceComponent}
            exclude={iris}
            onAddClass={onAddClass}
            viewLang={viewLang}
            defaultLang={defaultLang} />
          :
          null}>
        {output}
      </TitledPanel>
    );
  }
}

IndividualTypeDisplay.defaultProps = {
};

IndividualTypeDisplay.propTypes = {
  id: PropTypes.string.isRequired,
  isReadOnly: PropTypes.bool,
  isLoading: PropTypes.bool,
  source: PropTypes.string,
  textRef: PropTypes.string,
  defaultLang: PropTypes.string.isRequired,
  viewLang: PropTypes.string,
  components: PropTypes.array,
  graph: PropTypes.string.isRequired,
  sourceComponent: PropTypes.string.isRequired,
  projectIRI: PropTypes.string.isRequired,
  ontologyProjectAssociation: PropTypes.array,
  property: PropTypes.string,
  onAddClass: PropTypes.func,
  values: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string)
  ])
};

export default IndividualTypeDisplay;